import { useState, useEffect } from "react";
import { debounce } from "./asyncEffects";

/** @typedef HtmlElementInfo
 * @property {number} clientHeight
 * @property {number} clientWidth
 * @property {number} scrollTop
 * @property {number} scrollLeft
 * @property {number} scrollHeight
 * @property {number} scrollWidth
 */

/** @type {(ref: import("react").RefObject, deps?: any[]) => HtmlElementInfo} */
function useHtmlElementInfo(ref, deps = []) {
	const [info, setInfo] = useState(null);

	useEffect(() => {
		if (ref.current == null) return () => {};

		const updateInfo = debounce(() => {
			if (ref.current == null) return Promise.resolve();
			const {
				clientHeight,
				clientWidth,
				scrollTop,
				scrollLeft,
				scrollHeight,
				scrollWidth,
			} = ref.current;

			setInfo({
				clientHeight,
				clientWidth,
				scrollTop,
				scrollLeft,
				scrollHeight,
				scrollWidth,
			});

			return Promise.resolve();
		}, 150);

		updateInfo();

		ref.current.addEventListener("scroll", updateInfo, {
			capture: false,
			passive: true,
		});
		global.window.addEventListener("resize", updateInfo);

		return () => {
			if (ref.current != null) {
				ref.current.removeEventListener("scroll", updateInfo);
			}
			global.window.removeEventListener("resize", updateInfo);
		};
	}, [ref, ...deps]);

	return info;
}

export default useHtmlElementInfo;
